import React, { useState, useEffect } from 'react'
import { 
  User, Mail, Phone, MapPin, Briefcase, Calendar, 
  FileText, CheckCircle, XCircle, Eye, Download,
  AlertCircle, Clock, RefreshCw, ChevronDown, ChevronUp,
  Shield, Building, DollarSign
} from 'lucide-react'
import { supabase } from '../../services/supabase'
import { useAuth } from '../../context/AuthContext'
import { formatDate, formatCurrency } from '../../utils/formatters'
import toast from 'react-hot-toast'

const KYCReview = () => {
  const { profile } = useAuth()
  const [pendingUsers, setPendingUsers] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [expandedSections, setExpandedSections] = useState({
    personal: true,
    address: true,
    employment: true,
    documents: true
  })

  useEffect(() => {
    loadPendingUsers()
  }, [])

  const loadPendingUsers = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .in('kyc_status', ['pending', 'processing'])
        .order('kyc_submitted_at', { ascending: true })

      if (error) throw error
      setPendingUsers(data || [])
    } catch (error) {
      console.error('Error loading pending users:', error)
      toast.error('Failed to load pending verifications')
    } finally {
      setLoading(false)
    }
  }

  const loadUserDocuments = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('kyc_documents')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      if (error) throw error
      setDocuments(data || [])
    } catch (error) {
      console.error('Error loading documents:', error)
      toast.error('Failed to load documents')
    }
  }

  const handleSelectUser = async (user) => {
    setSelectedUser(user)
    await loadUserDocuments(user.id)
  }

  const handleApprove = async () => {
    if (!selectedUser) return

    setProcessing(true)
    try {
      // Update user KYC status
      const { error: updateError } = await supabase
        .from('users')
        .update({
          kyc_status: 'verified',
          kyc_verified_at: new Date().toISOString(),
          kyc_verified_by: profile.id,
          kyc_rejection_reason: null
        })
        .eq('id', selectedUser.id)

      if (updateError) throw updateError

      // Update all documents to approved
      await supabase
        .from('kyc_documents')
        .update({ status: 'approved' })
        .eq('user_id', selectedUser.id)

      // Create history record
      await supabase
        .from('kyc_history')
        .insert({
          user_id: selectedUser.id,
          action: 'approved',
          performed_by: profile.id,
          status: 'verified',
          notes: 'KYC verification approved by admin'
        })

      // Log activity
      await supabase
        .from('activity_logs')
        .insert({
          user_id: profile.id,
          action: 'kyc_approved',
          details: `Approved KYC verification for ${selectedUser.username || selectedUser.email}`
        })

      // Send notification to user
      await supabase
        .from('notifications')
        .insert({
          user_id: selectedUser.id,
          type: 'kyc_approved',
          title: 'KYC Verified',
          message: 'Your identity verification has been approved. You now have full access to your account.'
        })

      toast.success('User verification approved successfully!')
      setSelectedUser(null)
      await loadPendingUsers()
    } catch (error) {
      console.error('Error approving user:', error)
      toast.error('Failed to approve verification')
    } finally {
      setProcessing(false)
    }
  }

  const handleReject = async () => {
    if (!selectedUser || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason')
      return
    }

    setProcessing(true)
    try {
      // Update user KYC status
      const { error: updateError } = await supabase
        .from('users')
        .update({
          kyc_status: 'rejected',
          kyc_verified_by: profile.id,
          kyc_rejection_reason: rejectionReason
        })
        .eq('id', selectedUser.id)

      if (updateError) throw updateError

      // Update documents to rejected
      await supabase
        .from('kyc_documents')
        .update({ status: 'rejected', rejection_reason: rejectionReason })
        .eq('user_id', selectedUser.id)

      // Create history record
      await supabase
        .from('kyc_history')
        .insert({
          user_id: selectedUser.id,
          action: 'rejected',
          performed_by: profile.id,
          status: 'rejected',
          notes: rejectionReason
        })

      // Log activity
      await supabase
        .from('activity_logs')
        .insert({
          user_id: profile.id,
          action: 'kyc_rejected',
          details: `Rejected KYC verification for ${selectedUser.username || selectedUser.email}`
        })

      // Send notification to user
      await supabase
        .from('notifications')
        .insert({
          user_id: selectedUser.id,
          type: 'kyc_rejected',
          title: 'KYC Verification Rejected',
          message: `Your verification was rejected: ${rejectionReason}. Please resubmit with correct documents.`
        })

      toast.success('Verification rejected. User will be notified.')
      setShowRejectModal(false)
      setRejectionReason('')
      setSelectedUser(null)
      await loadPendingUsers()
    } catch (error) {
      console.error('Error rejecting user:', error)
      toast.error('Failed to reject verification')
    } finally {
      setProcessing(false)
    }
  }

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const getDocumentIcon = (type) => {
    if (type.includes('selfie')) return '📷'
    if (type.includes('license')) return '🪪'
    if (type.includes('passport')) return '🛂'
    return '📄'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
            KYC Verification Queue
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            {pendingUsers.length} user{pendingUsers.length !== 1 ? 's' : ''} awaiting verification
          </p>
        </div>
        <button
          onClick={loadPendingUsers}
          className="px-4 py-2 border-2 border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2 transition-colors w-full sm:w-auto justify-center"
        >
          <RefreshCw className="w-5 h-5" />
          Refresh
        </button>
      </div>

      {pendingUsers.length === 0 ? (
        <div className="bg-white dark:bg-dark-800 rounded-lg shadow-lg p-8 sm:p-12 text-center">
          <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            All Caught Up!
          </h3>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            There are no pending KYC verifications at the moment.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Users List */}
          <div className="lg:col-span-1 bg-white dark:bg-dark-800 rounded-lg shadow-lg p-3 sm:p-4 max-h-[400px] sm:max-h-[calc(100vh-16rem)] overflow-y-auto">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 text-sm sm:text-base">
              Pending Users
            </h3>
            <div className="space-y-2">
              {pendingUsers.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleSelectUser(user)}
                  className={`w-full text-left p-3 sm:p-4 rounded-lg transition-all ${
                    selectedUser?.id === user.id
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500'
                      : 'bg-gray-50 dark:bg-dark-700 hover:bg-gray-100 dark:hover:bg-dark-600 border-2 border-transparent'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white truncate">
                        {user.full_name || user.name || user.username || 'Unknown'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {user.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      user.kyc_status === 'pending' 
                        ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                        : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    }`}>
                      {user.kyc_status}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatDate(user.kyc_submitted_at)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* User Details */}
          <div className="lg:col-span-2">
            {!selectedUser ? (
              <div className="bg-white dark:bg-dark-800 rounded-lg shadow-lg p-8 sm:p-12 text-center">
                <User className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                  Select a user to review their verification
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* User Info Card */}
                <div className="bg-white dark:bg-dark-800 rounded-lg shadow-lg p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                          {selectedUser.full_name || selectedUser.name || 'Unknown'}
                        </h2>
                        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                          @{selectedUser.username || 'no-username'}
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 text-xs sm:text-sm rounded-full ${
                      selectedUser.kyc_status === 'pending'
                        ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                        : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    }`}>
                      {selectedUser.kyc_status}
                    </span>
                  </div>

                  {/* Personal Information */}
                  <div className="border-t border-gray-200 dark:border-dark-700 pt-4">
                    <button
                      onClick={() => toggleSection('personal')}
                      className="w-full flex items-center justify-between mb-3 hover:text-blue-600 transition-colors"
                    >
                      <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white flex items-center gap-2">
                        <User className="w-4 h-4 sm:w-5 sm:h-5" />
                        Personal Information
                      </h3>
                      {expandedSections.personal ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                    
                    {expandedSections.personal && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pl-0 sm:pl-7">
                        <div>
                          <label className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Email</label>
                          <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white break-all">{selectedUser.email}</p>
                        </div>
                        <div>
                          <label className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Phone</label>
                          <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white">{selectedUser.phone || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Date of Birth</label>
                          <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white">
                            {selectedUser.date_of_birth ? formatDate(selectedUser.date_of_birth) : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">SSN (Last 4)</label>
                          <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white">
                            ***-**-{selectedUser.ssn_last_4 || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Account Type</label>
                          <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white capitalize">
                            {selectedUser.account_type || 'Personal'}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Citizenship</label>
                          <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white">
                            {selectedUser.citizenship || selectedUser.country || 'N/A'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Address Information */}
                  <div className="border-t border-gray-200 dark:border-dark-700 pt-4 mt-4">
                    <button
                      onClick={() => toggleSection('address')}
                      className="w-full flex items-center justify-between mb-3 hover:text-blue-600 transition-colors"
                    >
                      <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white flex items-center gap-2">
                        <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
                        Address Information
                      </h3>
                      {expandedSections.address ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                    
                    {expandedSections.address && (
                      <div className="space-y-3 pl-0 sm:pl-7">
                        <div>
                          <label className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Full Address</label>
                          <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white">
                            {selectedUser.street_address || selectedUser.address || 'N/A'}<br />
                            {selectedUser.city && selectedUser.state && selectedUser.zip_code && (
                              `${selectedUser.city}, ${selectedUser.state} ${selectedUser.zip_code}`
                            )}
                          </p>
                        </div>
                        {selectedUser.address_duration && (
                          <div>
                            <label className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Duration at Address</label>
                            <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white capitalize">
                              {selectedUser.address_duration.replace(/_/g, ' ')}
                            </p>
                          </div>
                        )}
                        {selectedUser.previous_address && (
                          <div>
                            <label className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Previous Address</label>
                            <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white">
                              {selectedUser.previous_address}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Employment Information */}
                  <div className="border-t border-gray-200 dark:border-dark-700 pt-4 mt-4">
                    <button
                      onClick={() => toggleSection('employment')}
                      className="w-full flex items-center justify-between mb-3 hover:text-blue-600 transition-colors"
                    >
                      <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white flex items-center gap-2">
                        <Briefcase className="w-4 h-4 sm:w-5 sm:h-5" />
                        Employment Information
                      </h3>
                      {expandedSections.employment ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                    
                    {expandedSections.employment && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pl-0 sm:pl-7">
                        <div>
                          <label className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Employment Status</label>
                          <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white capitalize">
                            {selectedUser.employment_status?.replace(/_/g, ' ') || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Employer</label>
                          <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white">
                            {selectedUser.employer_name || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Job Title</label>
                          <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white">
                            {selectedUser.job_title || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Annual Income</label>
                          <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white capitalize">
                            {selectedUser.annual_income?.replace(/_/g, ' - $') || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Income Source</label>
                          <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white capitalize">
                            {selectedUser.income_source || 'N/A'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Documents */}
                <div className="bg-white dark:bg-dark-800 rounded-lg shadow-lg p-4 sm:p-6">
                  <button
                    onClick={() => toggleSection('documents')}
                    className="w-full flex items-center justify-between mb-4 hover:text-blue-600 transition-colors"
                  >
                    <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white flex items-center gap-2">
                      <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
                      Uploaded Documents ({documents.length})
                    </h3>
                    {expandedSections.documents ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>

                  {expandedSections.documents && (
                    documents.length === 0 ? (
                      <p className="text-center text-sm sm:text-base text-gray-500 dark:text-gray-400 py-4">
                        No documents uploaded
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        {documents.map((doc) => (
                          <div
                            key={doc.id}
                            className="border-2 border-gray-200 dark:border-dark-700 rounded-lg p-3 sm:p-4 hover:border-blue-500 transition-colors"
                          >
                            <div className="flex items-center gap-3 mb-3">
                              <span className="text-2xl sm:text-3xl">{getDocumentIcon(doc.document_type)}</span>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-sm sm:text-base text-gray-900 dark:text-white capitalize truncate">
                                  {doc.document_type.replace(/_/g, ' ')}
                                </h4>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {(doc.file_size / 1024).toFixed(2)} KB
                                </p>
                              </div>
                            </div>
                            <a
                              href={doc.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full px-3 py-2 text-sm border-2 border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg flex items-center justify-center gap-2 transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </a>
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </div>

                {/* Action Buttons */}
                <div className="bg-gray-50 dark:bg-dark-700 rounded-lg shadow-lg p-4 sm:p-6">
                  <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white mb-4">
                    Review Decision
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => setShowRejectModal(true)}
                      disabled={processing}
                      className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                    >
                      <XCircle className="w-5 h-5" />
                      Reject
                    </button>
                    <button
                      onClick={handleApprove}
                      disabled={processing}
                      className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                    >
                      <CheckCircle className="w-5 h-5" />
                      {processing ? 'Processing...' : 'Approve'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-800 rounded-xl max-w-md w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-4">
              Reject Verification
            </h3>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 flex gap-2 mb-4">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs sm:text-sm text-yellow-800 dark:text-yellow-300">
                The user will be notified and asked to resubmit documents.
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Reason for Rejection *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please explain why the verification was rejected..."
                className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                rows="4"
                required
              />
            </div>
            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false)
                  setRejectionReason('')
                }}
                className="flex-1 px-4 py-2 border-2 border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={processing || !rejectionReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {processing ? 'Processing...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default KYCReview